import { CAPABILITIES, type ActorSource } from '@lemontaigu/platform';
import { requireCapability } from './capabilityGate.js';
import type {
  ActivityCatalogStore,
  BoardActivityUpsert,
  ChannelCurationStore,
  DeskListStore,
  RegisterWriteStore,
  ShiftDecision,
  ShiftRequest,
  ShiftRequestInput,
  ShiftRequestStore,
  TimesheetWriteStore,
  TrustedPersonWriteStore,
  StaffingConfigStore,
  StaffingConfigSnapshot,
  MetricStore,
  StaffingProfileUpsert,
  StaffingPostUpsert,
  WorkContentUpsert,
} from './ports.js';
import type { DeskListEntry } from './domain.js';
import { rowsToMetrics, type MetricUpsert } from './metricRows.js';
import type { MetricDef } from './metricRegistry.js';

/**
 * DRIVING ports for the back-office's write use cases (ladder step 7).
 *
 * Thin, like `BoardPresenceService` and for the same reason: the invariants
 * these touch already live where they belong — FK RESTRICT for the activity
 * catalogue, the 0018 correction trigger for the clock, `SECURITY DEFINER` RPCs
 * for the register and the curation queue. A service layer that re-stated them
 * in TypeScript would be claiming a guarantee it cannot enforce against a
 * concurrent writer.
 *
 * What the seam buys is where authorization goes at step 9. Today every one of
 * these is gated by `is_admin` in a policy or inside an RPC, which works but is
 * invisible from the calling code; `assertCapability('rh.register.write')` at
 * the top of a method is the same rule, stated where a reader of the use case
 * can see it.
 *
 * DONE (2026-07-28, F3 L2 increment 3) — that last paragraph, delivered. Each
 * factory now takes an `ActorSource` and every method opens with
 * `requireCapability`. All four are operator-only use cases over EVERYONE's
 * rows, so the capability really is the whole rule here; the identity-scoped
 * counter-case is `BoardPresenceService`, which correctly has no gate (ladder
 * §3.19).
 */

export interface ActivityCatalogService {
  /** Create when `id` is null, update otherwise — one use case, « save this activity ». */
  saveActivity(id: number | null, input: BoardActivityUpsert): Promise<void>;
  /** Throws `ActivityInUseError` when presence history references it (0048). */
  deleteActivity(id: number, label: string): Promise<void>;
  /**
   * Toggle by INTENT, not by current state: the caller says whether the
   * employee should end up qualified, and this decides the statement. The view
   * previously passed the *existing* flag and inverted it at the call site,
   * which reads backwards and inverts wrongly under a stale render.
   */
  setQualified(activityId: number, employeeId: string, qualified: boolean): Promise<void>;
  setMain(activityId: number, employeeId: string, isMain: boolean): Promise<void>;
}

export function createActivityCatalogService(
  store: ActivityCatalogStore,
  actor: ActorSource,
): ActivityCatalogService {
  // `board_activities` + `board_activity_qualifications` — the catalogue itself,
  // never anybody's own row, so no identity path exists on these tables.
  const gate = () => requireCapability(actor, CAPABILITIES.RH_BOARD_MANAGE);
  return {
    saveActivity: async (id, input) => {
      gate();
      return id === null ? store.createActivity(input) : store.updateActivity(id, input);
    },
    deleteActivity: async (id, label) => {
      gate();
      return store.deleteActivity(id, label);
    },
    setQualified: async (activityId, employeeId, qualified) => {
      gate();
      return qualified
        ? store.addQualification(activityId, employeeId)
        : store.removeQualification(activityId, employeeId);
    },
    setMain: async (activityId, employeeId, isMain) => {
      gate();
      return store.setMainActivity(activityId, employeeId, isMain);
    },
  };
}

/**
 * Staffing configuration (predictive-staffing brief §4.5).
 *
 * Reads are ungated — a staffing requirement is coordination information, and
 * RLS already fences it behind `rh.board.read`. WRITES are `rh.board.manage`:
 * changing how many people an activity needs is the same class of act as
 * editing the activity itself, so it carries the same capability rather than a
 * new one (ADR-0013 — add a grant, not an axis, unless the axis is real).
 */
export interface StaffingConfigService {
  load(): Promise<StaffingConfigSnapshot>;
  saveProfile(id: number | null, input: StaffingProfileUpsert): Promise<number>;
  deleteProfile(id: number): Promise<void>;
  saveWorkContent(profileId: number, input: WorkContentUpsert): Promise<void>;
  saveBand(id: number | null, profileId: number, fromDemand: number): Promise<number>;
  deleteBand(id: number): Promise<void>;
  savePost(id: number | null, bandId: number, input: StaffingPostUpsert): Promise<void>;
  deletePost(id: number): Promise<void>;
  setCombination(activityA: number, activityB: number, combinable: boolean): Promise<void>;
}

export function createStaffingConfigService(
  store: StaffingConfigStore,
  actor: ActorSource,
): StaffingConfigService {
  const gate = () => requireCapability(actor, CAPABILITIES.RH_BOARD_MANAGE);
  return {
    load: async () => store.loadConfig(),
    saveProfile: async (id, input) => {
      gate();
      return store.saveProfile(id, input);
    },
    deleteProfile: async (id) => {
      gate();
      return store.deleteProfile(id);
    },
    saveWorkContent: async (profileId, input) => {
      gate();
      return store.saveWorkContent(profileId, input);
    },
    saveBand: async (id, profileId, fromDemand) => {
      gate();
      return store.saveBand(id, profileId, fromDemand);
    },
    deleteBand: async (id) => {
      gate();
      return store.deleteBand(id);
    },
    savePost: async (id, bandId, input) => {
      gate();
      return store.savePost(id, bandId, input);
    },
    deletePost: async (id) => {
      gate();
      return store.deletePost(id);
    },
    setCombination: async (activityA, activityB, combinable) => {
      gate();
      // Canonical order is the DB's CHECK constraint too — normalise here so a
      // caller passing (8, 7) does not get a constraint violation it cannot
      // interpret.
      const [a, b] = activityA < activityB ? [activityA, activityB] : [activityB, activityA];
      return combinable ? store.addCombination(a, b) : store.removeCombination(a, b);
    },
  };
}

export interface TimesheetCorrectionService {
  correctEntry(id: number, clockInIso: string, clockOutIso: string | null): Promise<void>;
  removeEntry(id: number): Promise<void>;
  addEntry(employeeId: string, clockInIso: string, clockOutIso: string | null): Promise<void>;
}

export function createTimesheetCorrectionService(
  store: TimesheetWriteStore,
  actor: ActorSource,
): TimesheetCorrectionService {
  // `rh.timesheet.write`, NOT `rh.timesheet.read`. Reading is identity-softened
  // (an employee reads their own hours without the capability); CORRECTING
  // someone's punches is operator-only and has no identity path — which is
  // exactly why the schema made them two capabilities.
  const gate = () => requireCapability(actor, CAPABILITIES.RH_TIMESHEET_WRITE);
  return {
    correctEntry: async (id, clockInIso, clockOutIso) => {
      gate();
      return store.updateEntry(id, clockInIso, clockOutIso);
    },
    removeEntry: async (id) => {
      gate();
      return store.deleteEntry(id);
    },
    addEntry: async (employeeId, clockInIso, clockOutIso) => {
      gate();
      return store.addEntry(employeeId, clockInIso, clockOutIso);
    },
  };
}

/**
 * « Hors Staff » (ADR-0024). Creating, archiving, PIN and retention are the same
 * act-class as the register (`rh.register.write`); correcting a punch is the same
 * act-class as any timesheet correction (`rh.timesheet.write`). Both are checked
 * again inside the RPCs — this states the rule where the use case is read.
 */
export interface TrustedPersonService {
  addPerson(firstName: string, lastName: string, nickname: string | null): Promise<string>;
  setArchived(id: string, archived: boolean): Promise<void>;
  setPin(id: string, pin: string): Promise<void>;
  setRetentionDays(days: number): Promise<void>;
  saveEntry(entryId: number | null, personId: string, clockInIso: string, clockOutIso: string | null): Promise<void>;
  removeEntry(entryId: number): Promise<void>;
}

/** The ceiling the operator set (2026-10-02); the database enforces the same range. */
export const TRUSTED_RETENTION_MIN_DAYS = 30;
export const TRUSTED_RETENTION_MAX_DAYS = 180;

export function createTrustedPersonService(
  store: TrustedPersonWriteStore,
  actor: ActorSource,
): TrustedPersonService {
  const registerGate = () => requireCapability(actor, CAPABILITIES.RH_REGISTER_WRITE);
  const timesheetGate = () => requireCapability(actor, CAPABILITIES.RH_TIMESHEET_WRITE);
  return {
    addPerson: async (firstName, lastName, nickname) => {
      registerGate();
      return store.create(firstName.trim(), lastName.trim(), nickname?.trim() || null);
    },
    setArchived: async (id, archived) => {
      registerGate();
      return store.archive(id, archived);
    },
    setPin: async (id, pin) => {
      registerGate();
      return store.setPin(id, pin);
    },
    setRetentionDays: async (days) => {
      registerGate();
      if (!Number.isInteger(days) || days < TRUSTED_RETENTION_MIN_DAYS || days > TRUSTED_RETENTION_MAX_DAYS) {
        throw new Error(
          `La durée de conservation doit être comprise entre ${TRUSTED_RETENTION_MIN_DAYS} et ${TRUSTED_RETENTION_MAX_DAYS} jours.`,
        );
      }
      return store.setRetentionDays(days);
    },
    saveEntry: async (entryId, personId, clockInIso, clockOutIso) => {
      timesheetGate();
      return store.setEntry(entryId, personId, clockInIso, clockOutIso);
    },
    removeEntry: async (entryId) => {
      timesheetGate();
      return store.deleteEntry(entryId);
    },
  };
}

/** The desk list page's use cases (ADR-0026). The read rides RLS
 *  (`rh.register.read`); putting someone on or off the list is a register write. */
export interface DeskListService {
  load(): Promise<DeskListEntry[]>;
  setListed(id: string, listed: boolean): Promise<void>;
}

export function createDeskListService(store: DeskListStore, actor: ActorSource): DeskListService {
  return {
    load: () => store.roster(),
    setListed: async (id, listed) => {
      requireCapability(actor, CAPABILITIES.RH_REGISTER_WRITE);
      return store.setListed(id, listed);
    },
  };
}

export interface RegisterService {
  savePerson(params: Record<string, unknown>): Promise<string>;
  /** New hire: person + first contract, atomically (0056, F15). */
  createHire(personParams: Record<string, unknown>, contractParams: Record<string, unknown>): Promise<string>;
  /** Create/replace a contract period (F15: not yet atomic with savePerson). */
  addContract(params: Record<string, unknown>): Promise<void>;
  saveContract(params: Record<string, unknown>): Promise<void>;
  /** The explicit "clôturer" act — see RegisterWriteStore.closeContract. */
  closeContract(params: Record<string, unknown>): Promise<void>;
  deleteContract(contractId: string): Promise<void>;
  setPin(employeeId: string, pin: string): Promise<void>;
  /** « Retirer tous les accès » — atomic, disables never deletes (ADR-0023). */
  revokeAccess(employeeId: string, reason: string | null): Promise<void>;
}

export function createRegisterService(
  store: RegisterWriteStore,
  actor: ActorSource,
): RegisterService {
  // The RUP — a legal document. Every one of these RPCs re-checks
  // `rh.register.write` itself (0064), so this is the same rule stated where the
  // use case is read, not a new one.
  const gate = () => requireCapability(actor, CAPABILITIES.RH_REGISTER_WRITE);
  return {
    savePerson: async (params) => {
      gate();
      return store.upsertPerson(params);
    },
    createHire: async (personParams, contractParams) => {
      gate();
      return store.createHire(personParams, contractParams);
    },
    addContract: async (params) => {
      gate();
      return store.upsertContract(params);
    },
    saveContract: async (params) => {
      gate();
      return store.updateContract(params);
    },
    closeContract: async (params) => {
      gate();
      return store.closeContract(params);
    },
    deleteContract: async (contractId) => {
      gate();
      return store.deleteContract(contractId);
    },
    setPin: async (employeeId, pin) => {
      gate();
      return store.setPin(employeeId, pin);
    },
    // Revoking is the same act-class as granting (« Créer l'accès ») and as
    // closing a contract — `rh.register.write`, not a new capability (ADR-0013).
    revokeAccess: async (employeeId, reason) => {
      gate();
      return store.revokeAccess(employeeId, reason);
    },
  };
}

/**
 * The employee's side of a shift request: send, withdraw, read their own.
 *
 * NOT capability-gated, on purpose — the same shape as `BoardPresenceService`
 * (ladder §3.19). It is reachable by IDENTITY: the RPC reads the caller's
 * employee link from the JWT, so there is no `employeeId` to forge on `send`
 * and nothing here a gate could add. The operator-only half is below.
 */
export interface ShiftRequestService {
  send(input: ShiftRequestInput): Promise<void>;
  cancel(id: number): Promise<void>;
  mine(employeeId: string): Promise<ShiftRequest[]>;
}

export function createShiftRequestService(store: ShiftRequestStore): ShiftRequestService {
  return {
    send: async (input) => {
      // Fast feedback only; the RPC re-checks everything (window, overlap,
      // contract) and is the authority.
      if (input.clockOutIso <= input.clockInIso) throw new Error('Le départ doit suivre l’arrivée');
      await store.send(input);
    },
    cancel: (id) => store.cancel(id),
    mine: (employeeId) => store.listForEmployee(employeeId),
  };
}

/**
 * The operator's inbox: read every request, accept as asked / modify / refuse.
 * `rh.timesheet.write` — deciding a request IS correcting the time record.
 */
export interface ShiftRequestDecisionService {
  pending(): Promise<ShiftRequest[]>;
  recent(limit?: number): Promise<ShiftRequest[]>;
  decide(id: number, decision: ShiftDecision, note?: string | null): Promise<void>;
}

export function createShiftRequestDecisionService(
  store: ShiftRequestStore,
  actor: ActorSource,
): ShiftRequestDecisionService {
  const gate = () => requireCapability(actor, CAPABILITIES.RH_TIMESHEET_WRITE);
  return {
    pending: async () => {
      gate();
      return store.listPending();
    },
    recent: async (limit = 30) => {
      gate();
      return store.listDecided(limit);
    },
    decide: async (id, decision, note = null) => {
      gate();
      if (decision.kind === 'modify' && decision.clockOutIso <= decision.clockInIso) {
        throw new Error('Le départ doit suivre l’arrivée');
      }
      return store.decide(id, decision, note && note.trim() !== '' ? note.trim() : null);
    },
  };
}

export interface ChannelCurationService {
  /**
   * `finalBodyFr` null = accept the AI's wording unchanged. The C9 posture is
   * that nothing is auto-saved, so « accept as written » is still an explicit
   * decision, not an absence of one.
   */
  accept(candidateId: string, finalBodyFr: string | null): Promise<void>;
  dismiss(candidateId: string): Promise<void>;
}

export function createChannelCurationService(
  store: ChannelCurationStore,
  actor: ActorSource,
): ChannelCurationService {
  const gate = () => requireCapability(actor, CAPABILITIES.RH_KNOWLEDGE_WRITE);
  return {
    accept: async (candidateId, finalBodyFr) => {
      gate();
      return store.acceptCandidate(candidateId, finalBodyFr);
    },
    dismiss: async (candidateId) => {
      gate();
      return store.dismissCandidate(candidateId);
    },
  };
}

/**
 * The metric registry (ADR-0015).
 *
 * Reads are ungated for the same reason staffing reads are: a load definition
 * is coordination information, and RLS already fences it behind
 * `rh.board.read`. Writes carry `rh.board.manage` — defining what « charge »
 * MEANS is the same class of act as editing the activity it gates.
 */
export interface MetricRegistryService {
  load(): Promise<MetricDef[]>;
  save(input: MetricUpsert): Promise<void>;
  remove(key: string): Promise<void>;
}

export function createMetricRegistryService(
  store: MetricStore,
  actor: ActorSource,
): MetricRegistryService {
  const gate = () => requireCapability(actor, CAPABILITIES.RH_BOARD_MANAGE);
  return {
    load: async () => rowsToMetrics(await store.load()),
    save: async (input) => {
      gate();
      return store.save(input);
    },
    remove: async (key) => {
      gate();
      return store.remove(key);
    },
  };
}
